import { Layer, Popup, Source } from "@vis.gl/react-maplibre";
import { FC, useMemo, useState } from "react";
import useEvent from "react-use-event-hook";

import {
  IWarkDistanceAndTime,
  latLngTuplesToLineStringFeature,
  leafletDashToMapLibreDashArray,
} from "@ryde/shared";

import { isPointerOverMapLibreMarker } from "./maplibre-pointer.utils";
import { MapLibreWalkingRouteTooltip } from "./maplibre-walking-route-tooltip";
import {
  WALKING_ROUTE_COLOR,
  WALKING_ROUTE_DASH_ARRAY,
  WALKING_ROUTE_HIT_WIDTH_PX,
  WALKING_ROUTE_MIN_POSITIONS,
  WALKING_ROUTE_POPUP_CLASS,
  WALKING_ROUTE_TOOLTIP_OFFSET_PX,
  WALKING_ROUTE_WIDTH_PX,
} from "./maplibre-walking-route.constants";
import { useMapLibreLayerPointer } from "./use-maplibre-layer-pointer";

/**
 * The dashed purple walk between two stations, with the distance/time card on hover — the MapLibre twin of
 * the Leaflet `<Polyline><Tooltip/></Polyline>` used by the passenger form and the passengers map.
 *
 * A second, transparent, much wider line sits under the visible one purely as a hit target: MapLibre hit
 * tests the drawn geometry, and a 3px dashed path is close to impossible to hover otherwise.
 */
export const MapLibreWalkingRoute: FC<{
  id: string;
  /** `[lat, lng]` pairs, as the walking-directions response returns them. */
  positions: [number, number][];
  distanceAndTime: IWarkDistanceAndTime;
}> = ({ id, positions, distanceAndTime }) => {
  const [hoveredAt, setHoveredAt] = useState<[number, number] | null>(null);
  const hitLayerId = `${id}-hit`;
  const hasLine = positions.length >= WALKING_ROUTE_MIN_POSITIONS;

  const feature = useMemo(
    () => latLngTuplesToLineStringFeature(positions),
    [positions],
  );

  const handleHover = useEvent(
    (
      _feature: unknown,
      event: {
        lngLat: { lat: number; lng: number };
        originalEvent?: { target?: EventTarget | null } | null;
      },
    ) => {
      // Station pins sit at the ends of the walk and overlap its hit line. A pin occludes the line, so
      // hovering one must not pop the walk card — only the line itself should.
      if (isPointerOverMapLibreMarker(event)) {
        setHoveredAt(null);
        return;
      }

      setHoveredAt([event.lngLat.lat, event.lngLat.lng]);
    },
  );
  const handleLeave = useEvent(() => setHoveredAt(null));

  useMapLibreLayerPointer({
    layerId: hitLayerId,
    enabled: hasLine,
    onHover: handleHover,
    onLeave: handleLeave,
  });

  if (!hasLine) {
    return null;
  }

  return (
    <>
      <Source id={id} type="geojson" data={feature}>
        <Layer
          id={hitLayerId}
          type="line"
          paint={{
            ["line-color"]: WALKING_ROUTE_COLOR,
            ["line-width"]: WALKING_ROUTE_HIT_WIDTH_PX,
            ["line-opacity"]: 0,
          }}
        />
        <Layer
          id={`${id}-line`}
          type="line"
          paint={{
            ["line-color"]: WALKING_ROUTE_COLOR,
            ["line-width"]: WALKING_ROUTE_WIDTH_PX,
            ["line-dasharray"]: leafletDashToMapLibreDashArray(
              WALKING_ROUTE_DASH_ARRAY,
              WALKING_ROUTE_WIDTH_PX,
            ),
          }}
        />
      </Source>

      {hoveredAt && (
        <Popup
          latitude={hoveredAt[0]}
          longitude={hoveredAt[1]}
          anchor="bottom"
          offset={WALKING_ROUTE_TOOLTIP_OFFSET_PX}
          closeButton={false}
          closeOnClick={false}
          className={WALKING_ROUTE_POPUP_CLASS}
        >
          <MapLibreWalkingRouteTooltip distanceAndTime={distanceAndTime} />
        </Popup>
      )}
    </>
  );
};
